import { PropsWithChildren } from "react";
import { LightbulbIcon } from "lucide-react";

import { clsxMerge } from "shared/lib/helpers";
import { GitbookMarkdownPreview } from "shared/ui/markdown/gitbook-preview";
import { Button } from "shared/ui/shadcn/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "shared/ui/shadcn/popover";
import { Skeleton } from "shared/ui/shadcn/skeleton";
import { TIME_INTERVALS } from "shared/lib/constants/time";

import { useQueryFetchGitbookDocs } from "@/api/routes/docs/queries";
import { DocsEmptyState } from "@/components/shared/docs/shared/empty-state";

const PendingState = () => (
  <div className="flex flex-col space-y-3">
    <Skeleton className="h-[120px] w-full" />
    <div className="space-y-2">
      <Skeleton className="h-4 w-[250px]" />
      <Skeleton className="h-4 w-[200px]" />
    </div>
  </div>
);

interface DocsTooltipPropsI extends PropsWithChildren {
  spaceId: string;
  path: string;
}

export const DocsTooltip = ({ children, spaceId, path }: DocsTooltipPropsI) => {
  const { data, isLoading, refetch, dataUpdatedAt } = useQueryFetchGitbookDocs(
    spaceId,
    path
  );

  const isDataStale =
    !dataUpdatedAt || Date.now() - dataUpdatedAt > TIME_INTERVALS.FIVE_MINUTES;

  const handleOnOpenChange = (isOpen: boolean) => {
    if (isOpen && isDataStale) {
      refetch();
    }
  };

  return (
    <Popover onOpenChange={handleOnOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          {children || <LightbulbIcon className="size-4" />}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="max-h-[300px] overflow-hidden overflow-y-auto">
        {!isLoading && !data?.markdown && <DocsEmptyState />}

        {isLoading ? (
          <PendingState />
        ) : (
          <div>
            <div className="typography-header-8">{data?.title}</div>

            <GitbookMarkdownPreview
              className={clsxMerge(
                "gitbook-markdown typography break-words",
                // the first child is the title, which we already show in the sidebar header
                "[&>*:first-child]:hidden"
              )}
              path={path}
            >
              {data?.markdown}
            </GitbookMarkdownPreview>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
